import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Card, Checkbox, Notice } from '@ailab/ui';
import { useMe } from '../lib/account';
import { apiRequest, errorMessage } from '../lib/http';
import { MY_LEGAL_QUERY_KEY, useMyLegal } from '../lib/legal';

/**
 * Si hay una versión nueva de las condiciones, la cuenta la acepta antes de operar
 * (RF-09.03). El servidor ya lo exige: esto solo enseña cómo hacerlo.
 */
export function LegalGate() {
  const me = useMe();
  const legal = useMyLegal(Boolean(me.data));
  const client = useQueryClient();
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const pending = legal.data?.pending ?? [];
  if (!me.data || pending.length === 0) return null;

  async function accept() {
    setBusy(true);
    setError(undefined);
    try {
      for (const doc of pending) {
        await apiRequest('/me/legal/acceptances', null, {
          method: 'POST',
          body: { kind: doc.kind, version: doc.version },
        });
      }
      await client.invalidateQueries({ queryKey: MY_LEGAL_QUERY_KEY });
    } catch (failure) {
      setError(errorMessage(failure));
      // Si la versión cambió mientras tanto, se vuelve a pedir la vigente.
      await client.invalidateQueries({ queryKey: MY_LEGAL_QUERY_KEY });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <Notice tone="info">
        Hay una versión nueva de las condiciones. Revísala y acéptala para seguir usando AILAB.
      </Notice>
      <ul>
        {pending.map((doc) => (
          <li key={`${doc.kind}-${doc.version}`}>
            <a href={doc.path} target="_blank" rel="noopener noreferrer">
              {doc.title}
            </a>{' '}
            (versión del {doc.version})
          </li>
        ))}
      </ul>
      <Checkbox checked={checked} onChange={(event) => setChecked(event.target.checked)}>
        He leído y acepto la versión vigente.
      </Checkbox>
      {error ? <Notice tone="error">{error}</Notice> : null}
      <Button type="button" busy={busy} disabled={!checked} onClick={() => void accept()}>
        Aceptar y continuar
      </Button>
    </Card>
  );
}
